import type {
  AgentRoleAssetKind,
  AgentRoleAssetScope,
  AgentRoleAssetStatus,
  DashboardRolloutMcpStatus,
  DashboardRolloutSetupStatus,
  DashboardRolloutTelemetryStatus,
  DashboardRolloutUpdateStatus,
  SkillProposalScope,
  SkillProposalSource,
  SkillProposalStatus,
  SkillProposalDeliveryState,
} from '../../apps/apis/claudeCodeDashboard/types';

export const HELP_TEXT = {
  window: 'Rolling time window used for every chart, count, and ranking on this page.',
  dailyActiveEmployees: 'Number of employees who produced Agent Plugin telemetry on each day.',
  topSkills: 'Most frequently observed skills from Claude Code or Codex telemetry.',
  topAgents: 'Most frequently observed sub-agents or agent personas used by employees.',
  topMcpTools: 'Most frequently observed MCP server/tool calls. Use this to see if internal data tools are used.',
  tokenUsageByEmployee: 'Token and cost totals grouped by employee. Raw prompts and outputs are not stored or shown.',
  invocations: 'Number of observed telemetry events for this skill, agent, or MCP tool.',
  employees: 'Distinct employees who used this item in the selected window.',
  sessions: 'Distinct agent sessions observed in the selected window.',
  events: 'Total telemetry events captured for this row or session.',
  inputTokens: 'Input tokens reported by the agent runtime or OTEL metrics when available.',
  outputTokens: 'Output tokens reported by the agent runtime or OTEL metrics when available.',
  costUsd: 'Estimated USD cost from available token/cost telemetry. Treat as directional, not billing truth.',
  roles: 'Employee-selected role packs installed by the Agent Plugin.',
  pluginVersion: 'Plugin version observed in telemetry. Non-semver values are treated as unknown or legacy senders.',
  lastSeen: 'Most recent telemetry event observed for this employee, version, or row.',
  mcpServer: 'MCP server name, for example skin-seoul or skin-seoul-ontology.',
  mcpTool: 'Specific MCP tool invoked inside the server.',
  telemetry: 'Whether recent telemetry was observed for the employee in the selected window.',
  setup: 'Latest setup/bootstrap status reported by the plugin.',
  update: 'Latest update check/apply status reported by the plugin.',
  mcpStatus: 'Whether MCP was selected, configured, or actually observed in telemetry.',
  ontology: 'Whether the ontology MCP is configured. This matters for ontology-first internal data lookup.',
  rolloutInstalled: 'Employees with Agent Plugin telemetry or registration evidence.',
  rolloutHealthy: 'Employees with recent telemetry in the selected window.',
  latestObserved: 'Highest plugin version observed in telemetry, not necessarily the static release manifest.',
  rolloutSetup: 'Employees reporting successful setup versus failed setup events.',
  rolloutMcp: 'Employees with MCP configured or used.',
  rolloutUpdates: 'Employees with updates available or failed update checks.',
  versionAdoption: 'Plugin version distribution across employees and sessions.',
  activeEmployees: 'Employees active in telemetry during the selected window.',
  roleAssetLibrary: 'Admin-managed shared skills, agents, hooks, instructions, and MCP presets distributed by role.',
  companyWideAssets: 'Assets available to every employee regardless of selected role.',
  roleSpecificAssets: 'Assets targeted to one or more selected role packs.',
  asset: 'Shared asset title and id. Open detail to inspect content, versions, source, and governance state.',
  assetKind: 'Type of shared asset: skill, agent, hook, instruction, or MCP preset.',
  assetScope: 'Distribution target for the asset: one role, multiple roles, or company-wide.',
  assetTargeting: 'Roles eligible to receive this shared asset.',
  assetVersion: 'Active version currently distributed versus latest version in the registry.',
  adoption: 'Eligible employees compared with employees who applied the asset locally.',
  usage: 'Runtime usage inferred from telemetry for this shared skill or agent.',
  failures: 'Delivery failures plus skipped deliveries reported by the plugin.',
  skillProposals: 'Employee-submitted local skill drafts waiting for personal or shared review.',
  proposalSource: 'Where the proposal came from. Local submissions are explicitly approved by employees.',
  proposalStatus: 'Current review lifecycle state of the proposal.',
  proposalRisk: 'Operational risk estimate for applying or sharing the generated skill.',
  proposalType: 'Proposal category, such as new personal skill or shared skill recommendation.',
  proposalTarget: 'Target adapter and skill id where the generated skill would be installed.',
  confidence: 'Model or deterministic confidence score for the recommendation.',
  safeEvidence: 'Privacy-safe summary. Raw employee prompts, inputs, outputs, and errors are not stored here.',
  recommendedAction: 'Suggested reviewer action generated with the proposal.',
  roleTargeting: 'Role recommendation context used when converting a proposal into a shared role asset.',
  generatedDraft: 'Generated SKILL.md content attached to this proposal for reviewer inspection.',
  reviewAction: 'Approve, reject, request revision, or convert the proposal into a shared role asset.',
  finalScope: 'Final distribution scope selected by the reviewer before approval.',
  selectedRoles: 'Comma-separated roles that will receive the shared asset when scope is role or multi-role.',
  reviewNote: 'Reviewer note stored with the lifecycle event for audit and future maintainers.',
  deliveryLifecycle: 'Employee machine check/apply telemetry for approved personal skill delivery.',
  governanceActions: 'Append-only lifecycle actions for shared role assets: publish, retire, or rollback.',
  assetContent: 'Versioned markdown/config content that will be installed by the plugin.',
  source: 'Proposal and asset identifiers that created this shared role asset.',
  usageMetrics: 'Delivery, usage, token, cost, and failure signals for this shared asset.',
  versionHistory: 'Historical versions for this role asset. Rollbacks create a new draft from a previous version.',
  highCostLowMcp:
    'Sessions with relatively high cost and little or no MCP usage. Review for coaching or better skills.',
  dailyCostTrend: 'Daily token cost trend for the selected window. Spikes are compared with the prior day.',
  costByRole: 'Cost grouped by selected employee roles.',
  costByModel: 'Cost grouped by model name when available.',
  sharedAssetHealthReview:
    'Metadata-only health review queue for shared role assets, based on adoption, usage, failure, and cost signals.',
  assetEvolutionReview:
    'Legacy name for the shared asset health review queue. Use Shared Asset Health in navigation and UI labels.',
  reviewCandidates: 'Shared assets that may need operational review based on current telemetry signals.',
  reviewQueueWindow: 'Lookback window used to compute adoption, usage, failure, and cost review candidates.',
  reviewQueueLimit: 'Maximum number of candidates requested from the backend before local filters are applied.',
  reviewQueueAction: 'Recommended maintenance action for the shared asset.',
  reviewQueueRisk: 'Estimated operational risk of leaving this asset as-is or changing it.',
  suggestedNextStep: 'Plain-English reviewer guidance for what to do next.',
} as const;

export const SKILL_PROPOSAL_STATUS_HELP: Record<SkillProposalStatus, string> = {
  pending_review: 'Waiting for employee/admin review.',
  approved: 'Approved as a personal skill for the employee.',
  revision_requested: 'Reviewer asked for changes before approval.',
  converted_to_shared: 'Converted into a shared role/company asset review path.',
  rejected: 'Rejected and should not be installed or shared.',
  delivered: 'Approved asset was observed as delivered/applied on an employee machine.',
};

export const SKILL_PROPOSAL_SOURCE_HELP: Record<SkillProposalSource, string> = {
  local_submission: 'Employee explicitly escalated a local skill draft. No raw prompt/output text is uploaded.',
  telemetry_batch: 'Legacy centralized batch proposal source. Kept for historical review only.',
};

export const SKILL_PROPOSAL_SCOPE_HELP: Record<SkillProposalScope, string> = {
  personal: 'Only the submitting employee should receive this skill.',
  role: 'One selected role should receive the shared asset.',
  multi_role: 'Multiple selected roles should receive the shared asset.',
  company_wide: 'All employees should receive the shared asset.',
};

export const RISK_HELP = {
  low: 'Low operational risk. Usually safe for personal installation or narrow sharing.',
  medium: 'Medium risk. Review content and targeting before approving.',
  high: 'High risk. Requires careful review before any install or sharing.',
} as const;

export const ROLE_ASSET_STATUS_HELP: Record<AgentRoleAssetStatus, string> = {
  draft: 'Created but not distributed yet.',
  active: 'Currently eligible for plugin distribution.',
  retired: 'No longer distributed by the plugin.',
};

export const ROLE_ASSET_SCOPE_HELP: Record<AgentRoleAssetScope, string> = {
  role: 'Distributed to one role.',
  multi_role: 'Distributed to multiple selected roles.',
  company_wide: 'Distributed to all employees.',
};

export const ROLE_ASSET_KIND_HELP: Record<AgentRoleAssetKind, string> = {
  skill: 'Reusable workflow instruction installed as a skill.',
  agent: 'Shared specialist agent definition.',
  hook: 'Automation hook installed into Claude Code or Codex.',
  instruction: 'Global or role-specific instruction text.',
  mcp_preset: 'MCP server selection/configuration preset.',
};

export const ROLLOUT_TELEMETRY_HELP: Record<DashboardRolloutTelemetryStatus, string> = {
  healthy: 'Recent telemetry was observed in the selected window.',
  quiet: 'Some telemetry exists, but recent activity is low.',
  silent: 'No recent telemetry observed. The user may be inactive or hooks may be broken.',
};

export const ROLLOUT_MCP_HELP: Record<DashboardRolloutMcpStatus, string> = {
  configured: 'MCP servers are configured in plugin setup.',
  used: 'MCP usage was observed in telemetry.',
  missing: 'No MCP configuration or usage was observed.',
};

export const ROLLOUT_SETUP_HELP: Record<DashboardRolloutSetupStatus, string> = {
  completed: 'Setup/bootstrap completed successfully.',
  failed: 'Setup/bootstrap failure was reported.',
  unknown: 'No setup status has been observed yet.',
};

export const ROLLOUT_UPDATE_HELP: Record<DashboardRolloutUpdateStatus, string> = {
  applied: 'Latest update was applied successfully.',
  available: 'An update was detected but not yet applied.',
  failed: 'Update check or apply failed.',
  checked: 'Update check ran without applying anything.',
  unknown: 'No update status has been observed yet.',
};

export const DELIVERY_STATE_HELP: Record<SkillProposalDeliveryState, string> = {
  not_ready: 'Proposal is not yet approved for delivery.',
  approved_pending_apply: 'Approved, but the employee machine has not applied it yet.',
  applied: 'Employee machine reported the approved skill as applied.',
};

export const EVENT_TYPE_HELP: Record<string, string> = {
  SessionStart: 'Agent session started or resumed.',
  SessionEnd: 'Agent session ended.',
  Stop: 'Agent run stopped.',
  PreToolUse: 'Hook fired before a tool call.',
  PostToolUse: 'Hook fired after a tool call.',
  UserPromptSubmit: 'Prompt submit hook. Raw prompt text is not collected by SkinSeoul telemetry.',
};

export const ASSET_EVOLUTION_ACTION_HELP = {
  investigate_failures: 'Failures or skipped deliveries are high enough to require investigation.',
  review_instructions: 'Usage or cost signals suggest the shared instruction/asset may need refinement.',
  improve_onboarding: 'Adoption is low, so employees may need clearer onboarding or better default setup.',
} as const;
